import { useCallback, useEffect, useMemo, useState } from "react";
import { NextPage } from "next";
import Head from "next/head";
import classnames from "classnames";
import { CategorizedItems, categorizeItems } from "~/submodules/categorizeItems";
import { useRouter } from "next/router";
import { format, fromUnixTime } from "date-fns";
import { useUser } from "~/context/userContext";
import {
  useListClassroomsQuery,
  useFollowUserMutation,
  useUnfollowUserMutation,
  useListUserArchivesQuery,
  useListCategoriesQuery,
} from "~/hooks/rtk-query-api";
import { Archive, Classroom } from "~/generated/rtk-query/nowdo-live-api.generated";
import { getInitialPageProps } from "~/store";
import { splitArray } from "~/submodules/splitArray";
import { assertsString } from "~/submodules/assertsString";
import { defaultListUserArchivesLimit, loadUserPageData } from "~/components/links/UserPageLink";
import { useUserProfile } from "~/hooks/useUserProfile";
import { redirect } from "~/submodules/redirect";
import { ClassroomsWrapper, ClassroomsWrapperTitle } from "~/components/aud/ui/ClassroomsWrapper";
import { Section } from "~/components/aud/base/Section";
import { ProfileButton } from "~/components/aud/ui/UserProfileModal/ProfileButton";
import { ClassroomState } from "~/types/classroom";
import { SignUpModal } from "~/components/aud/ui/SignUpModeal";
import { CardsContainer } from "~/components/aud/ui/CardsContainer";
import { MiniArchiveCards } from "~/components/aud/ui/MiniArchiveCards";
import { isNullish } from "~/submodules/isNullish";
import css from "./index.module.scss";
import { Image } from "~/components/Image";

const useInfinityListUserArchivesQuery = (userId: string) => {
  const [cursor, setCursor] = useState<string | null>(null);
  const [limit, setLimit] = useState(defaultListUserArchivesLimit);
  const userArchivesQuery = useListUserArchivesQuery({
    id: userId,
    ...(cursor ? { cursor } : {}),
    limit,
  });
  const [userArchives, setUserArchives] = useState<Archive[]>(userArchivesQuery.data?.archives ?? []);

  useEffect(() => {
    const _userArchives = userArchivesQuery.data?.archives;
    if (_userArchives && _userArchives !== userArchives) {
      setUserArchives((v) => [...v, ..._userArchives]);
    }
  }, [userArchivesQuery.data]);

  const fetchNextUserArchives = useCallback(() => {
    if (!userArchivesQuery.data?.cursor) {
      return;
    }
    setLimit(Math.round(defaultListUserArchivesLimit / 2));
    setCursor(userArchivesQuery.data.cursor);
  }, [userArchivesQuery.data?.cursor]);

  return {
    userArchives,
    fetchNextUserArchives,
  };
};

const UserPage: NextPage = () => {
  const router = useRouter();
  const userId = router.query.id as string;
  const { user, isAnonymous } = useUser();
  const [profile, profileState] = useUserProfile(userId);
  const categoriesQuery = useListCategoriesQuery();
  const liveAndScheduledClassrooms = useListClassroomsQuery({ userId });
  const [follow, followState] = useFollowUserMutation();
  const [unfollow, unfollowState] = useUnfollowUserMutation();
  const { userArchives, fetchNextUserArchives } = useInfinityListUserArchivesQuery(userId);
  const [openSignUpModal, setOpenSignUpModal] = useState(false);

  useEffect(() => {
    if (!profile) {
      router.push("/");
    }
  }, [user]);

  const [liveClassrooms, scheduledClassrooms] = useMemo((): [Classroom[], Classroom[]] => {
    if (!liveAndScheduledClassrooms.data) {
      return [[], []];
    }
    return splitArray(
      liveAndScheduledClassrooms.data?.classrooms,
      (classroom) => classroom.state === ClassroomState.opened
    );
  }, [liveAndScheduledClassrooms.data]);

  const categorizedArchives: (CategorizedItems<Archive> & {
    hasScroll: (has: boolean) => unknown;
  })[] = useMemo(() => {
    if (categoriesQuery.data && userArchives.length > 0) {
      const categorized = categorizeItems(categoriesQuery.data.categories, userArchives);
      let counter = 0;
      return categorized.map((item) => ({
        ...item,
        hasScroll: (canScroll) => {
          if (!canScroll) {
            counter++;
          }
          if (categorized.length === counter) {
            return fetchNextUserArchives();
          }
        },
      }));
    }
    return [];
  }, [userArchives, categoriesQuery.data?.categories, fetchNextUserArchives]);

  const isLoading = followState.isLoading || unfollowState.isLoading || profileState.isFetching;
  // 開催予定のクラスルーム欄に開催中のクラスルームも出しますが、これは仕様です
  const displayClassrooms = [...liveClassrooms, ...scheduledClassrooms];

  if (isNullish(profile)) {
    return null;
  }

  const profileButtonProps = {
    target: "_self",
    loading: isLoading,
    onFollowClick: async () => {
      if (isAnonymous) {
        setOpenSignUpModal(true);
        return;
      }
      await follow({ id: userId }).unwrap();
    },
    onUnfollowClick: () => unfollow({ id: userId }).unwrap(),
    profile,
  };

  return (
    <>
      <main className={css.main}>
        <Head>
          <title>ユーザーページ</title>
        </Head>
        <Section>
          <div className={css.avatarArea}>
            <div className={classnames([css.maxWidth, css.avatarWrapper])}>
              <Image
                src={profile.avatarUrl}
                className={css.avatar}
                width={124}
                height={165}
                alt={`Profile picture of ${profile.name}`}
              />
            </div>
          </div>
          <div className={css.maxWidth}>
            <div className={css.nameJobButton}>
              <div className={css.nameJob}>
                <div className={css.name}>{profile.name}</div>
                <div>{profile.professionalTitle}</div>
              </div>
              <ProfileButton {...profileButtonProps} className={classnames([css.profileButton, "_only__pc"])} />
            </div>
            <div className={css.followWrapper}>
              <span className={css.follow}>
                <strong>{profile.followerCount}</strong>
                <label className={css.subtitle}>フォロワー</label>
              </span>
              <span className={css.follow}>
                <strong>{profile.followingCount}</strong>
                <label className={css.subtitle}>フォロー中</label>
              </span>
            </div>
            {(profile.isProfessional || profile.isPremium || profile.isAdult) && (
              <div className={css.badgeArea}>
                {profile.isProfessional && (
                  <span className={classnames([css.badge, css.green, css.mgRight8px])}>プロフェッショナル</span>
                )}
                {profile.isPremium && (
                  <span className={classnames([css.badge, css.red, css.mgRight8px])}>NowDoプレミアム</span>
                )}
                {!profile.isProfessional && profile.isAdult && (
                  <span className={classnames([css.badge, css.lightgray])}>25歳以上</span>
                )}
              </div>
            )}
            <div className={css.spProfileButton}>
              <ProfileButton {...profileButtonProps} className={css.profileButton} />
            </div>
            <p className={css.introduction}>{profile.introduction}</p>
            <div className={css.registerAt}>
              <div className={css.subtitle}>登録日</div>
              <div className={css.registerAtData}>{format(fromUnixTime(profile.registeredAt), "yyyy年MM月dd日")}</div>
            </div>
          </div>
          <div>
            {displayClassrooms.length > 0 && (
              <ClassroomsWrapper title="開催予定のクラスルーム" classrooms={displayClassrooms} />
            )}
          </div>
          {categorizedArchives.length > 0 && (
            <div>
              <ClassroomsWrapperTitle noBorder>アーカイブ動画</ClassroomsWrapperTitle>
              <div>
                {categorizedArchives.map((category) => (
                  <CardsContainer
                    key={category.categoryId}
                    title={category.categoryName}
                    scrollToRightEnd={fetchNextUserArchives}
                    hasScroll={category.hasScroll}
                  >
                    <MiniArchiveCards archives={category.items} />
                  </CardsContainer>
                ))}
              </div>
            </div>
          )}
        </Section>
      </main>
      <SignUpModal
        open={openSignUpModal}
        onClose={() => setOpenSignUpModal(false)}
        onSuccess={() => {
          window.location.reload();
        }}
      />
    </>
  );
};

UserPage.getInitialProps = getInitialPageProps((store) => async (context) => {
  const userId = context.query.id;
  assertsString(userId);

  try {
    await loadUserPageData(store, userId);
  } catch (e) {
    // ユーザーが見つからなかったら404ページに飛ばします
    if (e.status === 404) {
      redirect(context, "/404");
    }
    if (500 <= e.status && e.status <= 599) {
      redirect(context, "/500");
    }
  }
  return {};
});

export default UserPage;
